import { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { ImageIcon, UploadIcon, XIcon } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Button from './form/Button';

interface ImageUploadProps {
  onImageUpload: (file: File) => Promise<string | null>;
  onImageRemove?: () => void;
  currentImageUrl?: string | null;
  className?: string;
  maxSizeMB?: number;
  label?: string;
  shape?: 'square' | 'circle';
  isUploading?: boolean;
}

export default function ImageUpload({
  onImageUpload,
  onImageRemove,
  currentImageUrl,
  className = '',
  maxSizeMB = 1.5,
  label = '',
  shape = 'square',
  isUploading = false
}: ImageUploadProps) {
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const maxSizeBytes = maxSizeMB * 1024 * 1024; // Convert MB to bytes
  
  // Reset preview when currentImageUrl changes
  useEffect(() => {
    setPreview(currentImageUrl || null);
  }, [currentImageUrl]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setError(null);
    
    if (acceptedFiles.length === 0) return;
    
    const file = acceptedFiles[0];
    
    // Check file size
    if (file.size > maxSizeBytes) {
      const errorMessage = `File size exceeds ${maxSizeMB}MB limit`;
      setError(errorMessage);
      toast.error(errorMessage);
      return;
    }
    
    try {
      const imageUrl = await onImageUpload(file);
      
      // If imageUrl is returned, update the preview
      if (imageUrl) {
        setPreview(imageUrl);
      }
      // If null is returned, it means the actual upload will happen after cropping
    } catch (error) {
      console.error('Upload error:', error);
      setError('Failed to process image. Please try again.');
      toast.error('Failed to process image');
      // Reset preview on error
      setPreview(currentImageUrl || null);
    }
  }, [onImageUpload, maxSizeBytes, maxSizeMB, currentImageUrl]);
  
  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': [],
      'image/png': []
    },
    maxFiles: 1,
    maxSize: maxSizeBytes,
    disabled: isUploading
  });
  
  // Handle file rejections (e.g., file too large)
  useEffect(() => {
    if (fileRejections.length > 0) {
      const rejection = fileRejections[0];
      
      // Check if the file was rejected due to size
      const sizeError = rejection.errors.find(e => e.code === 'file-too-large');
      
      if (sizeError) {
        const errorMessage = `File size exceeds ${maxSizeMB}MB limit`;
        setError(errorMessage);
        toast.error(errorMessage);
      } else {
        // Handle other rejection reasons
        const errorMessage = rejection.errors[0]?.message || 'Invalid file';
        setError(errorMessage);
        toast.error(errorMessage);
      }
    }
  }, [fileRejections, maxSizeMB]);
  
  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the parent div's onClick
    setPreview(null);
    setError(null);
    if (onImageRemove) {
      onImageRemove();
    }
  };
  
  const containerClasses = `
    relative flex flex-col items-center justify-center 
    border-2 border-dashed rounded-lg
    transition-colors
    ${isDragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-primary-400'}
    ${className}
  `;
  
  const imageContainerClasses = `
    relative overflow-hidden bg-gray-100 w-full h-full
    ${shape === 'circle' ? 'rounded-full' : 'rounded-lg'}
  `;

  return (
    <div className="space-y-2">
      {label && <label className="block text-sm font-medium text-gray-700">{label}</label>}
      
      <div className={containerClasses}>
        {isUploading ? (
          <div className="flex flex-col items-center justify-center p-4 h-full w-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mb-2"></div>
            <p className="text-sm text-gray-500">Uploading...</p>
          </div>
        ) : preview ? (
          <div className="relative h-full w-full">
            <div className={imageContainerClasses}>
              <img 
                src={preview} 
                alt="Preview" 
                className={`object-cover w-full h-full ${shape === 'circle' ? 'aspect-square' : ''}`}
              />
            </div>
            <button
              type="button"
              onClick={handleRemove}
              className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
            >
              <XIcon className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div {...getRootProps()} className="w-full h-full flex flex-col items-center justify-center cursor-pointer p-4">
            <input {...getInputProps()} />
            <ImageIcon className="h-12 w-12 text-gray-400 mb-2" />
            <p className="text-sm text-gray-500 text-center">
              {isDragActive ? 'Drop the image here' : 'Drag & drop an image here, or click to select'}
            </p>
            <p className="text-xs text-gray-400 mt-1 text-center">
              Supported formats: JPG, PNG (max {maxSizeMB}MB)
            </p>
            <Button 
              type="button" 
              variant="outline" 
              size="sm" 
              className="mt-4"
              icon={<UploadIcon className="h-4 w-4" />}
            >
              Select Image
            </Button>
          </div>
        )}
      </div>
      
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}